import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Create axios instance with better defaults
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Add authorization token to requests if available
const getAuthHeaders = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user && user.token) {
      return { Authorization: `Bearer ${user.token}` };
    }
    return {};
  } catch (error) {
    return {};
  }
};

// Helper function to handle API errors consistently
const handleApiError = (error, customMessage = "An error occurred") => {
  // Return a user-friendly error message
  return {
    message: customMessage,
    status: error.response?.status || 500,
    isError: true,
  };
};

// ============================================================================
// SEARCH & DISCOVERY
// ============================================================================

/**
 * Search papers with advanced filters
 * @param {Object} params - Search parameters
 * @param {string} params.query - Search query
 * @param {number} params.page - Page number
 * @param {number} params.limit - Results per page
 * @param {number} params.yearFrom - Filter by year from (optional)
 * @param {number} params.yearTo - Filter by year to (optional)
 * @param {number} params.minCitations - Minimum citations (optional)
 * @param {number} params.maxCitations - Maximum citations (optional)
 * @param {boolean} params.openAccessOnly - Open access only filter (optional)
 * @param {string} params.topics - Comma-separated topics (optional)
 * @param {string} params.journal - Journal name filter (optional)
 * @param {string} params.publisher - Publisher name filter (optional)
 * @param {string} params.sort - Sort order: relevance, citations, newest, oldest, title (optional)
 */
export const searchPapers = async (query, page, limit, filters) => {
  try {
    // Support both old and new API formats
    let searchParams = {};

    // Old format compatibility (query, page, limit, filters)
    if (typeof query === "string") {
      searchParams = {
        query,
        page: page || 1,
        limit: limit || 10,
        ...(filters?.field && { field: filters.field }),
        ...(filters?.year && { year: filters.year }),
        ...(filters?.sortBy && { sort: filters.sortBy }),
      };
    } else {
      // New format - direct params object
      const params = query; // First argument is params object
      searchParams = {
        query: params.query,
        page: params.page || 1,
        limit: params.limit || 10,
        // Advanced filters (all optional)
        ...(params.yearFrom && { year_from: params.yearFrom }),
        ...(params.yearTo && { year_to: params.yearTo }),
        ...(params.minCitations && { min_citations: params.minCitations }),
        ...(params.maxCitations && { max_citations: params.maxCitations }),
        ...(params.openAccessOnly !== undefined && {
          open_access: params.openAccessOnly,
        }),
        ...(params.topics && { topics: params.topics }),
        ...(params.journal && { journal: params.journal }),
        ...(params.publisher && { publisher: params.publisher }),
        ...(params.sort && { sort: params.sort }),
        // Old format compatibility
        ...(params.field && { field: params.field }),
        ...(params.year && { year: params.year }),
        ...(params.sortBy && { sort: params.sortBy }),
      };
    }

    const headers = getAuthHeaders();
    const response = await apiClient.get(`/papers/search`, {
      params: searchParams,
      headers,
    });

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to search papers");
    throw errorData;
  }
};

export const getPaperByDoi = async (doi) => {
  try {
    // Try the standard endpoint first
    try {
      const response = await apiClient.get(
        `/papers/${encodeURIComponent(doi)}`
      );
      return response.data;
    } catch (mainError) {
      // If that fails, try an alternative endpoint format
      const fallbackResponse = await apiClient.get(`/papers/detail`, {
        params: { doi },
      });
      return fallbackResponse.data;
    }
  } catch (error) {
    const errorData = handleApiError(error, `Failed to fetch paper details`);
    throw errorData;
  }
};

/**
 * Get trending papers
 * @param {Object} params - Parameters
 * @param {string} params.timeframe - Timeframe: week, month, all (default: week)
 * @param {string} params.field - Field filter (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Results per page (default: 20)
 */
export const getTrendingPapers = async (params = {}) => {
  try {
    const headers = getAuthHeaders();
    const response = await apiClient.get(`/papers/trending`, {
      params: {
        timeframe: params.timeframe || "week",
        ...(params.field && { field: params.field }),
        page: params.page || 1,
        limit: params.limit || 20,
      },
      headers,
    });

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to load trending papers");
    throw errorData;
  }
};

/**
 * Get personalized recommendations (requires authentication)
 * @param {number} limit - Number of recommendations (default: 10)
 */
export const getRecommendations = async (limit = 10) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      // If not authenticated, fallback to trending papers
      return getTrendingPapers({ limit, timeframe: "week" });
    }

    const response = await apiClient.get(`/papers/recommendations`, {
      params: { limit },
      headers,
    });

    return response.data;
  } catch (error) {
    // Fallback to trending papers
    try {
      return await getTrendingPapers({ limit, timeframe: "week" });
    } catch (fallbackError) {
      const errorData = handleApiError(
        fallbackError,
        "Failed to load recommendations"
      );
      throw errorData;
    }
  }
};

// Backward compatibility alias
export const getRecommendedPapers = getRecommendations;

// ============================================================================
// SAVE/UNSAVE OPERATIONS
// ============================================================================

// Save a paper to user's saved papers collection
export const savePaper = async (paperId, paperData = null) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to save papers");
    }

    let requestBody = {};

    // If paperId is provided, send only that
    if (paperId) {
      requestBody = { paperId };
    }
    // Otherwise, send the full paper data
    else if (paperData) {
      requestBody = { paperData };
    } else {
      throw new Error("Either paperId or paperData must be provided");
    }

    const response = await apiClient.post("/saved", requestBody, { headers });
    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to save paper");
    throw errorData;
  }
};

// Unsave a paper from user's saved papers collection
export const unsavePaper = async (paperId) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to unsave papers");
    }

    const response = await apiClient.delete("/saved", {
      headers,
      data: { paperId },
    });

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to unsave paper");
    throw errorData;
  }
};

// Get all saved papers for a user
export const getSavedPapers = async (userId, page = 1, limit = 10) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to fetch saved papers");
    }

    const response = await apiClient.get(`/saved/${userId}`, {
      headers,
      params: { page, limit },
    });

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to fetch saved papers");
    throw errorData;
  }
};

/**
 * Bulk save papers (up to 50)
 * @param {Array<string>} paperIds - Array of paper IDs or DOIs
 */
export const bulkSavePapers = async (paperIds) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to save papers");
    }

    const response = await apiClient.post(
      "/saved/bulk",
      { paperIds },
      { headers }
    );

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to bulk save papers");
    throw errorData;
  }
};

/**
 * Bulk unsave papers (up to 50)
 * @param {Array<string>} paperIds - Array of paper IDs or DOIs
 */
export const bulkUnsavePapers = async (paperIds) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to unsave papers");
    }

    const response = await apiClient.delete("/saved/bulk", {
      headers,
      data: { paperIds },
    });

    return response.data;
  } catch (error) {
    const errorData = handleApiError(error, "Failed to bulk unsave papers");
    throw errorData;
  }
};

/**
 * Check if a paper is saved
 * @param {string} paperId - Paper ID or DOI
 */
export const checkIfSaved = async (paperId) => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      return false;
    }

    const response = await apiClient.get(`/papers/saved/${paperId}`, {
      headers,
    });

    return response.data.isSaved || false;
  } catch (error) {
    return false;
  }
};

// Backward compatibility alias
export const checkIfPaperIsSaved = checkIfSaved;

// ============================================================================
// EXPORT
// ============================================================================

/**
 * Export saved papers
 * @param {string} userId - User ID
 * @param {string} format - Export format: bibtex, ris, csv, json (default: bibtex)
 */
export const exportSavedPapers = async (userId, format = "bibtex") => {
  try {
    const headers = getAuthHeaders();

    if (!headers.Authorization) {
      throw new Error("Authentication required to export papers");
    }

    const response = await apiClient.get(`/saved/${userId}/export`, {
      params: { format },
      headers,
      responseType: "blob",
    });

    // Create download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = downloadUrl;

    const extensions = {
      bibtex: "bib",
      ris: "ris",
      csv: "csv",
      json: "json",
    };

    link.setAttribute(
      "download",
      `saved_papers.${extensions[format] || format}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, message: "Export downloaded successfully" };
  } catch (error) {
    const errorData = handleApiError(error, "Failed to export papers");
    throw errorData;
  }
};

// ============================================================================
// ANALYTICS
// ============================================================================

/**
 * Track paper view
 * @param {string} paperId - Paper ID or DOI
 */
export const trackPaperView = async (paperId) => {
  try {
    const headers = getAuthHeaders();

    await apiClient.post(`/papers/${paperId}/view`, {}, { headers });
  } catch (error) {
    // Don't throw error for tracking - it's not critical
  }
};

// ============================================================================
// NEW BACKEND FEATURES - SEARCH ENHANCEMENTS
// ============================================================================

/**
 * Get autocomplete suggestions for search
 * @param {string} query - Search query (minimum 2 characters)
 * @returns {Promise<{suggestions: Array<{type: string, text: string, paperId?: string, citations?: number}>}>}
 */
export const getSuggestions = async (query) => {
  try {
    if (!query || query.length < 2) {
      return { suggestions: [] };
    }

    const headers = getAuthHeaders();
    const response = await apiClient.get(`/papers/suggestions`, {
      params: { q: query },
      headers,
    });

    return response.data;
  } catch (error) {
    return { suggestions: [] };
  }
};

/**
 * Get related papers for a specific paper
 * @param {string} paperId - Paper ID or DOI
 * @returns {Promise<{relatedPapers: Array<{title: string, similarity: number, citationCount: number, ...}>}>}
 */
export const getRelatedPapers = async (paperId) => {
  try {
    if (!paperId) {
      return { relatedPapers: [] };
    }

    const headers = getAuthHeaders();
    const response = await apiClient.get(
      `/papers/${encodeURIComponent(paperId)}/related`,
      { headers }
    );

    return response.data;
  } catch (error) {
    return { relatedPapers: [] };
  }
};

/**
 * Get trending topics across the platform
 * @returns {Promise<{topics: Array<{topic: string, paperCount: number, avgCitations: number}>}>}
 */
export const getTrendingTopics = async () => {
  try {
    const headers = getAuthHeaders();
    const response = await apiClient.get(`/papers/trending-topics`, {
      headers,
    });

    return response.data;
  } catch (error) {
    return { topics: [] };
  }
};
